import CloseRounded from '@mui/icons-material/CloseRounded'
import TipsAndUpdatesRounded from '@mui/icons-material/TipsAndUpdatesRounded'
import ChatRounded from '@mui/icons-material/ChatRounded'
import PlayCircleOutlineRounded from '@mui/icons-material/PlayCircleOutlineRounded'
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded'
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import { useLocation, useNavigate } from 'react-router-dom'
import petIdea from '../../../assets/petIdea.png'
import petConfuse from '../../../assets/petConfuse.png'
import petExcited from '../../../assets/petExcited.png'
import petHappy from '../../../assets/petHappy.png'
import petYes from '../../../assets/petYes.png'
import petLuca from '../../../assets/petLuca.png'
import robot from '../../../assets/robot.png'
import { useGlobalContext } from '../../../context/GlobalState'
import { usePlanPermissions } from '../../../hooks/usePlanPermissions'
import { useSidebarContext } from '../../layouts/sideBar/SidebarContext'
import AssistantPetChatPanel from './AssistantPetChatPanel'
import { getAssistantPetContext, getOnboardingTourSteps } from './assistantPetMessages'
import userApi from '../../../api/user.api'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '../../../queries/querykeys'
import { useQueryGetUserMe } from '../../../queries/userQueries'


const ASSISTANT_PET_DISPLAY_KEY = 'assistantPetDisplayStateV1'
const DRAG_CLICK_THRESHOLD = 6
const DRAG_VIEWPORT_PADDING = 12
// Animaciones Ãºnicas por estado del personaje
const PET_CONFIGS = Object.freeze([
  {
    image: petIdea,
    // Idea: destello â€” sube rÃ¡pido, gira levemente, baja despacio
    animate: { y: [0, -18, -14, -18, 0], rotate: [0, -4, 4, -2, 0], scale: [1, 1.06, 1.04, 1.06, 1] },
    transition: { duration: 2.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.8 }
  },
  {
    image: petConfuse,
    // Confuse: inclinaciÃ³n lenta de lado a lado ("hmm...")
    animate: { rotate: [0, 8, 0, -8, 0], y: [0, 2, 0, 2, 0] },
    transition: { duration: 3.8, repeat: Infinity, ease: 'easeInOut' }
  },
  {
    image: petExcited,
    // Excited: rebote energÃ©tico con ligero squash
    animate: { y: [0, -16, -4, -16, 0], scale: [1, 1.05, 0.97, 1.05, 1] },
    transition: { duration: 0.75, repeat: Infinity, ease: [0.33, 1, 0.68, 1] }
  },
  {
    image: petHappy,
    // Happy: flotaciÃ³n suave y balanceo gentil
    animate: { y: [0, -8, 0], rotate: [0, 1.5, -1.5, 0], scale: [1, 1.02, 1] },
    transition: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' }
  },
  {
    image: petYes,
    // Yes: asentimiento doble â€” baja, sube, baja suave, vuelve
    animate: { y: [0, 8, 0, 5, 0], scaleY: [1, 0.94, 1, 0.97, 1], scaleX: [1, 1.03, 1, 1.01, 1] },
    transition: { duration: 1.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.6 }
  },
  {
    image: petLuca,
    // Welcome: flotaciÃ³n suave y balanceo gentil (saludo)
    animate: { y: [0, -8, 0], rotate: [0, 1.5, -1.5, 0], scale: [1, 1.02, 1] },
    transition: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' }
  }
])

const PET_STATES_MAP = Object.freeze({
  idea: 0,
  confuse: 1,
  excited: 2,
  happy: 3,
  yes: 4,
  welcome: 5
})

const getPetConfigForContext = contextId => {
  if (!contextId) return PET_CONFIGS[3] // petHappy por defecto

  let hash = 0

  for (let index = 0; index < contextId.length; index += 1) {
    hash = (hash * 31 + contextId.charCodeAt(index)) >>> 0
  }

  // Usamos modulo 5 para que el hashing excluya petLuca y mantenga exactamente los mismos comportamientos anteriores de la ayuda contextual
  return PET_CONFIGS[hash % 5] || PET_CONFIGS[3]
}

const DISPLAY_STATES = Object.freeze({
  expanded: 'expanded',
  minimized: 'minimized'
})

const readStoredDisplayState = () => {
  if (typeof window === 'undefined') return true

  const storedDisplayState = window.localStorage.getItem(ASSISTANT_PET_DISPLAY_KEY)

  if (storedDisplayState === DISPLAY_STATES.minimized) return false
  if (storedDisplayState === DISPLAY_STATES.expanded) return true

  return true
}

const persistDisplayState = isExpanded => {
  window.localStorage.setItem(
    ASSISTANT_PET_DISPLAY_KEY,
    isExpanded ? DISPLAY_STATES.expanded : DISPLAY_STATES.minimized
  )
}

const positionsAreEqual = (leftPosition, rightPosition) =>
  leftPosition?.x === rightPosition?.x && leftPosition?.y === rightPosition?.y

const getExpandedDefaultPosition = ({ isMobile, sidebarWidth, isTourActive }) => ({
  x: isMobile ? 12 : sidebarWidth + 16,
  y: isMobile ? 88 : (isTourActive ? 96 : 140)
})

const AssistantPet = () => {
  const { pathname, search } = useLocation()
  const { currentBusiness } = useGlobalContext()

  const queryClient = useQueryClient()
  const { data: userMe } = useQueryGetUserMe()
  const { hasPermission, hasPlan, subscriptionExpired } = usePlanPermissions()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { isExpanded } = useSidebarContext()
  const [isPetExpanded, setIsPetExpanded] = useState(readStoredDisplayState)
  const [hasCustomPosition, setHasCustomPosition] = useState(false)
  const [position, setPosition] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [expandedVideoUrl, setExpandedVideoUrl] = useState(null)
  const [highlightRect, setHighlightRect] = useState(null)
  const [doNotShowAgain, setDoNotShowAgain] = useState(false)
  const [hasInitializedCheckbox, setHasInitializedCheckbox] = useState(false)
  const [isHoveringMascot, setIsHoveringMascot] = useState(false)
  const [isHoveringTourTooltip, setIsHoveringTourTooltip] = useState(false)
  const [minimizedPosition, setMinimizedPosition] = useState(null)
  const [isDraggingMinimized, setIsDraggingMinimized] = useState(false)

  useEffect(() => {
    if (userMe?.extra && !hasInitializedCheckbox) {
      const initialTourVal = userMe.extra.initialTour !== false
      setDoNotShowAgain(!initialTourVal)
      setHasInitializedCheckbox(true)
    }
  }, [userMe, hasInitializedCheckbox])
  const widgetRef = useRef(null)
  const dragStartRef = useRef(null)
  const previousContextIdRef = useRef(null)
  const minimizedWidgetRef = useRef(null)
  const minimizedDragStartRef = useRef(null)
  const minimizedJustDraggedRef = useRef(false)

  const navigate = useNavigate()
  const tourSteps = useMemo(() => getOnboardingTourSteps(), [])

  const [isTourActive, setIsTourActive] = useState(() => {
    if (typeof window === 'undefined') return false
    // Flag de reinicio de sesión (sobrevive navigate sin afectar localStorage)
    if (sessionStorage.getItem('luca_tour_session_restart') === 'true') {
      sessionStorage.removeItem('luca_tour_session_restart')
      return true
    }
    const isActive = window.localStorage.getItem('luca_tour_active')
    if (isActive === 'true') return true
    if (isActive === 'false') return false
    
    const isCompleted = window.localStorage.getItem('luca_tour_completed')
    return isCompleted !== 'true'
  })

  const [tourStepIndex, setTourStepIndex] = useState(() => {
    if (typeof window === 'undefined') return 0
    const storedIndex = window.localStorage.getItem('luca_tour_step_index')
    return storedIndex ? parseInt(storedIndex, 10) : 0
  })

  const activeStep = isTourActive ? tourSteps[tourStepIndex] : null

  useEffect(() => {
    // Si hay bandera de reinicio de sesión, activar el tour
    if (sessionStorage.getItem('luca_tour_session_restart') === 'true') {
      sessionStorage.removeItem('luca_tour_session_restart')
      setIsTourActive(true)
      setIsPetExpanded(true)
      localStorage.setItem('luca_tour_active', 'true')
      return
    }
    
    const isActive = localStorage.getItem('luca_tour_active')
    if (isActive === 'true') {
      setIsTourActive(true)
      setIsPetExpanded(true)
      return
    }
    if (isActive === 'false') {
      return
    }

    const isCompleted = localStorage.getItem('luca_tour_completed')
    if (isCompleted !== 'true') {
      setIsTourActive(true)
      localStorage.setItem('luca_tour_active', 'true')
      setIsPetExpanded(true)
    }
  }, [])

  // ─── Spotlight: scroll + highlight + reposition mascot ────────────────────
  useEffect(() => {
    if (!isTourActive || !activeStep?.targetElementId) {
      setHighlightRect(null)
      return
    }

    // Small delay to let navigation/render settle
    const timeout = setTimeout(() => {
      const el = document.getElementById(activeStep.targetElementId)
      if (!el) {
        setHighlightRect(null)
        return
      }

      // Scroll the chart into the visible area
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })

      // Wait for scroll to finish before measuring position
      const measureTimeout = setTimeout(() => {
        const rect = el.getBoundingClientRect()
        setHighlightRect({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height
        })

        // Reposition mascot only if the step defines a petSide
        if (activeStep.petSide) {
          const petSide = activeStep.petSide
          let petX, petY
          if (petSide === 'right') {
            petX = Math.min(window.innerWidth - 350, rect.right - 80)
            petY = Math.max(12, rect.top - 30)
          } else {
            petX = Math.max(12, rect.left - 60)
            petY = Math.max(12, rect.top - 30)
          }
          setPosition({ x: petX, y: petY })
          setHasCustomPosition(true)
        }
      }, 500)

      return () => clearTimeout(measureTimeout)
    }, 300)

    return () => clearTimeout(timeout)
  }, [isTourActive, activeStep, tourStepIndex])

  const hasBillingAccess =
    hasPlan &&
    !subscriptionExpired &&
    hasPermission('allowIntelligentBilling') &&
    Boolean(currentBusiness?.tokenFacturas)

  const context = useMemo(
    () => getAssistantPetContext(pathname, search, { includeBilling: hasBillingAccess }),
    [hasBillingAccess, pathname, search]
  )

  const displayContext = useMemo(() => {
    if (isTourActive && activeStep) {
      return {
        id: activeStep.id,
        title: activeStep.title,
        description: activeStep.description,
        detail: activeStep.detail,
        detailIcon: activeStep.detailIcon
      }
    }
    return context
  }, [isTourActive, activeStep, context])

  const activePetConfig = useMemo(() => {
    if (isTourActive && activeStep && activeStep.petState) {
      const index = PET_STATES_MAP[activeStep.petState] ?? 3
      return PET_CONFIGS[index]
    }
    const activeId = isTourActive && activeStep ? activeStep.id : displayContext.id
    return getPetConfigForContext(activeId)
  }, [isTourActive, activeStep, displayContext.id])

  const sidebarWidth = isMobile ? 56 : isExpanded ? 256 : 80
  const expandedDefaultPosition = useMemo(
    () => getExpandedDefaultPosition({ isMobile, sidebarWidth, isTourActive }),
    [isMobile, sidebarWidth, isTourActive]
  )
  const expandedPanelStyle = useMemo(
    () => ({
      width: isMobile
        ? 'min(calc(100vw - 24px), 34rem)'
        : `min(34rem, calc(100vw - ${sidebarWidth + 32}px))`
    }),
    [isMobile, sidebarWidth]
  )

  const getMinimizedDefault = () => ({
    x: typeof window !== 'undefined' ? (isMobile ? 16 : 24) : 0,
    y: typeof window !== 'undefined' ? (isMobile ? -16 : -24) : 0
  })

  const clampMinimizedPosition = useCallback(nextPosition => {
    if (typeof window === 'undefined') return nextPosition

    const widgetWidth = minimizedWidgetRef.current?.offsetWidth || 210
    const widgetHeight = minimizedWidgetRef.current?.offsetHeight || 80

    // Because the element is bottom-0 left-0, x is positive to move right and y is negative to move up
    const minX = DRAG_VIEWPORT_PADDING
    const maxX = (window.innerWidth - widgetWidth) - DRAG_VIEWPORT_PADDING

    const minY = - (window.innerHeight - widgetHeight) + DRAG_VIEWPORT_PADDING
    const maxY = - DRAG_VIEWPORT_PADDING

    return {
      x: Math.min(Math.max(nextPosition.x, minX), maxX),
      y: Math.min(Math.max(nextPosition.y, minY), maxY)
    }
  }, [])

  const clampPosition = useCallback(nextPosition => {
    if (typeof window === 'undefined') return nextPosition

    const widgetWidth = widgetRef.current?.offsetWidth || 80
    const widgetHeight = widgetRef.current?.offsetHeight || 80
    const maxX = Math.max(
      DRAG_VIEWPORT_PADDING,
      window.innerWidth - widgetWidth - DRAG_VIEWPORT_PADDING
    )
    const maxY = Math.max(
      DRAG_VIEWPORT_PADDING,
      window.innerHeight - widgetHeight - DRAG_VIEWPORT_PADDING
    )

    return {
      x: Math.min(Math.max(nextPosition.x, DRAG_VIEWPORT_PADDING), maxX),
      y: Math.min(Math.max(nextPosition.y, DRAG_VIEWPORT_PADDING), maxY)
    }
  }, [])

  const resetExpandedPosition = useCallback(() => {
    setHasCustomPosition(false)
    setPosition(expandedDefaultPosition)
    setIsDragging(false)
    dragStartRef.current = null
  }, [expandedDefaultPosition])

  useEffect(() => {
    if (previousContextIdRef.current === null) {
      previousContextIdRef.current = displayContext.id
      return
    }

    if (previousContextIdRef.current === displayContext.id) return

    previousContextIdRef.current = displayContext.id
    setIsChatOpen(false)

    if (!isPetExpanded) return

    persistDisplayState(true)
    if (isTourActive && activeStep?.petAlignRight) {
      const rightX = Math.max(12, window.innerWidth - 560)
      setPosition({ x: rightX, y: 96 })
      setHasCustomPosition(true)
    } else {
      resetExpandedPosition()
    }
    setIsPetExpanded(true)
  }, [displayContext.id, isPetExpanded, resetExpandedPosition])

  const syncPositionWithinViewport = useCallback(() => {
    setPosition(previousPosition => {
      const basePosition =
        hasCustomPosition && previousPosition ? previousPosition : expandedDefaultPosition
      const clampedPosition = clampPosition(basePosition)

      return positionsAreEqual(clampedPosition, basePosition) ? basePosition : clampedPosition
    })
  }, [clampPosition, expandedDefaultPosition, hasCustomPosition])

  useEffect(() => {
    if (isPetExpanded && !hasCustomPosition) {
      setPosition(expandedDefaultPosition)
    }
  }, [expandedDefaultPosition, hasCustomPosition, isPetExpanded])

  useEffect(() => {
    if (typeof window === 'undefined' || !isPetExpanded) return undefined

    const frameId = window.requestAnimationFrame(syncPositionWithinViewport)
    window.addEventListener('resize', syncPositionWithinViewport)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.removeEventListener('resize', syncPositionWithinViewport)
    }
  }, [isPetExpanded, syncPositionWithinViewport])

  // Minimized: sync drag position on window resize and chat open
  useEffect(() => {
    if (typeof window === 'undefined' || isPetExpanded) return undefined

    const syncMinimized = () => {
      setMinimizedPosition(prev => clampMinimizedPosition(prev || getMinimizedDefault()))
    }

    window.addEventListener('resize', syncMinimized)
    // Run sync when chat panel toggles to keep it inside viewport
    syncMinimized()

    return () => window.removeEventListener('resize', syncMinimized)
  }, [isPetExpanded, isChatOpen, clampMinimizedPosition])

  const handleMinimize = () => {
    persistDisplayState(false)
    setIsDragging(false)
    dragStartRef.current = null
    setIsPetExpanded(false)
    setMinimizedPosition(null)
  }

  const handleExpand = () => {
    persistDisplayState(true)
    resetExpandedPosition()
    setIsChatOpen(false)
    setIsPetExpanded(true)
  }

  const toggleChatPanel = () => {
    setIsChatOpen(previousState => !previousState)
  }

  const handleNextStep = () => {
    if (tourStepIndex < tourSteps.length - 1) {
      const nextIndex = tourStepIndex + 1
      setTourStepIndex(nextIndex)
      localStorage.setItem('luca_tour_step_index', nextIndex.toString())
      localStorage.setItem('luca_tour_active', 'true')
      const nextStep = tourSteps[nextIndex]
      setExpandedVideoUrl(null)
      setHighlightRect(null)
      if (!nextStep.targetElementId) {
        if (nextStep.petAlignRight) {
          const rightX = Math.max(12, window.innerWidth - 560)
          setPosition({ x: rightX, y: 96 })
          setHasCustomPosition(true)
        } else {
          setHasCustomPosition(false)
          resetExpandedPosition()
        }
      }
      if (nextStep.target) {
        navigate({ pathname: nextStep.target.pathname, search: nextStep.target.search || '' })
      }
    } else {
      handleCompleteTour()
    }
  }

  const handlePrevStep = () => {
    if (tourStepIndex > 0) {
      const prevIndex = tourStepIndex - 1
      setTourStepIndex(prevIndex)
      localStorage.setItem('luca_tour_step_index', prevIndex.toString())
      localStorage.setItem('luca_tour_active', 'true')
      const prevStep = tourSteps[prevIndex]
      setExpandedVideoUrl(null)
      setHighlightRect(null)
      if (!prevStep.targetElementId) {
        if (prevStep.petAlignRight) {
          const rightX = Math.max(12, window.innerWidth - 560)
          setPosition({ x: rightX, y: 96 })
          setHasCustomPosition(true)
        } else {
          setHasCustomPosition(false)
          resetExpandedPosition()
        }
      }
      if (prevStep.target) {
        navigate({ pathname: prevStep.target.pathname, search: prevStep.target.search || '' })
      }
    }
  }

  const handleSkipTour = async () => {
    try {
      await userApi.updateUserMeExtra({
        initialTour: !doNotShowAgain
      })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
    } catch (error) {
      console.error("Error al actualizar preferencia en el servidor:", error)
    }
    
    if (doNotShowAgain) {
      localStorage.setItem('luca_tour_completed', 'true')
    } else {
      localStorage.setItem('luca_tour_completed', 'false')
    }
    localStorage.setItem('luca_tour_active', 'false')
    localStorage.removeItem('luca_tour_step_index')
    sessionStorage.removeItem('luca_tour_session_restart')
    setIsTourActive(false)
    setHighlightRect(null)
    resetExpandedPosition()
  }

  const handleCompleteTour = async () => {
    try {
      await userApi.updateUserMeExtra({
        initialTour: !doNotShowAgain
      })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
    } catch (error) {
      console.error("Error al actualizar preferencia en el servidor:", error)
    }

    if (doNotShowAgain) {
      localStorage.setItem('luca_tour_completed', 'true')
    } else {
      localStorage.setItem('luca_tour_completed', 'false')
    }
    localStorage.setItem('luca_tour_active', 'false')
    localStorage.removeItem('luca_tour_step_index')
    sessionStorage.removeItem('luca_tour_session_restart')
    setIsTourActive(false)
    setHighlightRect(null)
    resetExpandedPosition()
  }

  // Reinicia el tour solo para esta sesión (no modifica la preferencia permanente)
  const handleRestartTour = () => {
    setTourStepIndex(0)
    localStorage.setItem('luca_tour_step_index', '0')
    localStorage.setItem('luca_tour_active', 'true')
    // sessionStorage: bandera que sobrevive navigate() por si el componente se remonta
    // Solo guardamos en sessionStorage si no estamos ya en la vista inicial para evitar banderas colgadas
    if (pathname !== '/') {
      sessionStorage.setItem('luca_tour_session_restart', 'true')
    }
    setIsTourActive(true)
    setIsPetExpanded(true)
    setDoNotShowAgain(false)
    setHasInitializedCheckbox(false)
    setHighlightRect(null)
    setIsHoveringMascot(false)
    navigate('/')
  }

  const handleDragStart = () => {
    dragStartRef.current = position || expandedDefaultPosition
    setIsDragging(true)
  }

  const handleDrag = (_event, dragData) => {
    setPosition(
      clampPosition({
        x: dragData.x,
        y: dragData.y
      })
    )
  }

  const handleDragStop = (_event, dragData) => {
    const nextPosition = clampPosition({
      x: dragData.x,
      y: dragData.y
    })
    const startPosition = dragStartRef.current || expandedDefaultPosition
    const movedDistanceX = Math.abs(nextPosition.x - startPosition.x)
    const movedDistanceY = Math.abs(nextPosition.y - startPosition.y)
    const didDrag = movedDistanceX > DRAG_CLICK_THRESHOLD || movedDistanceY > DRAG_CLICK_THRESHOLD

    setPosition(nextPosition)
    setIsDragging(false)
    dragStartRef.current = null

    if (didDrag) {
      setHasCustomPosition(true)
    }
  }

  const draggablePosition = position || expandedDefaultPosition

  const handleMinimizedDragStart = () => {
    minimizedDragStartRef.current = minimizedPosition || getMinimizedDefault()
    setIsDraggingMinimized(true)
  }

  const handleMinimizedDrag = (_event, dragData) => {
    setMinimizedPosition(clampMinimizedPosition({ x: dragData.x, y: dragData.y }))
  }

  const handleMinimizedDragStop = (_event, dragData) => {
    const nextPosition = clampMinimizedPosition({ x: dragData.x, y: dragData.y })
    const startPosition = minimizedDragStartRef.current || getMinimizedDefault()
    const movedDistanceX = Math.abs(nextPosition.x - startPosition.x)
    const movedDistanceY = Math.abs(nextPosition.y - startPosition.y)
    const didDrag = movedDistanceX > DRAG_CLICK_THRESHOLD || movedDistanceY > DRAG_CLICK_THRESHOLD

    setMinimizedPosition(nextPosition)
    setIsDraggingMinimized(false)
    minimizedDragStartRef.current = null

    if (didDrag) {
      minimizedJustDraggedRef.current = true
      setTimeout(() => { minimizedJustDraggedRef.current = false }, 150)
    }
  }

  const minimizedDraggablePosition = minimizedPosition || getMinimizedDefault()

  return (
    <>
      <AnimatePresence>
        {isTourActive && (
          <motion.div
            key='tour-overlay'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className={`fixed inset-0 z-[1329] pointer-events-auto ${highlightRect ? '' : 'bg-black/50'}`}
            aria-hidden
          />
        )}
      </AnimatePresence>

      {/* Spotlight highlight around target chart */}
      <AnimatePresence>
        {highlightRect && (
          <motion.div
            key='tour-highlight'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='fixed z-[1330] pointer-events-none rounded-2xl'
            style={{
              top: highlightRect.top - 8,
              left: highlightRect.left - 8,
              width: highlightRect.width + 16,
              height: highlightRect.height + 16,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.50), 0 0 30px 4px rgba(99,102,241,0.35)',
              border: '2px solid rgba(255,255,255,0.25)',
            }}
          />
        )}
      </AnimatePresence>
      <div className='fixed inset-0 z-[1330] pointer-events-none'>
        <AnimatePresence mode='wait'>
          {isPetExpanded ? (
            <motion.div
              key={`assistant-pet-expanded-${displayContext.id}`}
              initial={{ opacity: 0, x: -18, y: 8 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, x: -12, y: 6 }}
              transition={{ duration: 0.24, ease: 'easeOut' }}
              className='absolute inset-0 pointer-events-none'
            >
              <Draggable
                bounds='parent'
                handle='.assistant-pet-drag-handle'
                nodeRef={widgetRef}
                position={draggablePosition}
                onStart={handleDragStart}
                onDrag={handleDrag}
                onStop={handleDragStop}
              >
                <aside
                  ref={widgetRef}
                  style={expandedPanelStyle}
                  className='absolute pointer-events-auto'
                >
                  <div className='grid grid-cols-[7rem_minmax(0,1fr)] items-center gap-1 sm:grid-cols-[12rem_minmax(0,1fr)] sm:gap-0'>
                    <motion.div
                      animate={
                        isDragging
                          ? { y: 0, scale: 1, rotate: 0, scaleX: 1, scaleY: 1 }
                          : activePetConfig.animate
                      }
                      transition={
                        isDragging
                          ? { duration: 0 }
                          : activePetConfig.transition
                      }
                      className={`assistant-pet-drag-handle relative z-10 flex items-center justify-end touch-none ${
                        isDragging ? 'cursor-grabbing' : 'cursor-grab'
                      }`}
                    >
                      <div
                        className='absolute inset-x-4 bottom-5 h-10 rounded-full bg-primary/25 blur-2xl'
                        aria-hidden
                      />
                      <div
                        className='absolute inset-x-2 inset-y-6 -z-10 rounded-full bg-primary/20 blur-3xl'
                        aria-hidden
                      />

                      <div className='relative flex flex-col items-center'>
                        <div className='relative'>
                          <div
                            className='absolute inset-x-6 top-6 bottom-8 rounded-full bg-gradient-to-b from-white/70 via-primary/10 to-transparent blur-2xl'
                            aria-hidden
                          />
                          <img
                            src={activePetConfig.image}
                            alt='Luca AI'
                            draggable={false}
                            className='relative h-40 w-auto object-contain drop-shadow-[0_18px_30px_rgba(15,23,42,0.22)] sm:h-56'
                          />
                        </div>
                      </div>
                    </motion.div>

                      <div className='relative -ml-2 flex items-center sm:-ml-8'>
                        <div
                          className='absolute left-3 top-1/2 h-4 w-4 -translate-x-[70%] -translate-y-1/2 rotate-45 border-b border-l border-slate-200/80 bg-white/95 shadow-sm sm:left-5 sm:h-5 sm:w-5'
                          aria-hidden
                        />

                    <div className='relative rounded-[30px] border border-slate-200/80 bg-white/95 p-4 shadow-2xl backdrop-blur-sm sm:p-5'>
                      <button
                        type='button'
                        onClick={isTourActive ? handleSkipTour : handleMinimize}
                        className='absolute right-4 top-4 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
                        aria-label='Minimizar ayuda de LUCA'
                        title='Minimizar'
                      >
                        <CloseRounded style={{ fontSize: 18 }} />
                      </button>

                      <AnimatePresence mode='wait' initial={false}>
                        <motion.div
                          key={displayContext.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                        >
                          <h3 className='mt-3 pr-8 text-lg font-semibold text-slate-900 sm:text-xl'>
                            {displayContext.title || 'LUCA'}
                          </h3>
                          <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                            {displayContext.description}
                          </p>
                          {isTourActive && activeStep?.bullets?.length > 0 && (
                            <ol className='mt-2 flex flex-col gap-1 pl-5 list-decimal'>
                              {activeStep.bullets.map((bullet, idx) => (
                                <li key={idx} className='text-sm font-sans leading-6 text-slate-600'>
                                  {bullet}
                                </li>
                              ))}
                            </ol>
                          )}
                          {displayContext.detail ? (
                            isTourActive && displayContext.detailIcon ? (
                              <p className='mt-2 flex items-start gap-1.5 text-sm font-sans leading-6 text-slate-500'>
                                <TipsAndUpdatesRounded sx={{ fontSize: 16, color: '#f59e0b', flexShrink: 0, mt: '2px' }} />
                                <span>{displayContext.detail}</span>
                              </p>
                            ) : (
                              <p className='mt-2 text-sm font-sans leading-6 text-slate-500'>
                                {displayContext.detail}
                              </p>
                            )
                          ) : null}

                          {isTourActive && activeStep?.links?.length > 0 && (
                            <div className='mt-3 flex flex-col gap-2'>
                              {activeStep.links.map(link => (
                                <div key={link.label} className='flex flex-col'>
                                  {link.type === 'video' && (
                                    <>
                                      <button
                                        type='button'
                                        onClick={() =>
                                          setExpandedVideoUrl(prev =>
                                            prev === link.url ? null : link.url
                                          )
                                        }
                                        className='inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary hover:text-primary/80 transition pointer-events-auto w-fit'
                                      >
                                        <PlayCircleOutlineRounded sx={{ fontSize: 16 }} />
                                        {link.label}
                                      </button>
                                      <AnimatePresence>
                                        {expandedVideoUrl === link.url && link.url !== '#' && (
                                          <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.25, ease: 'easeOut' }}
                                            className='overflow-hidden mt-2 rounded-xl'
                                          >
                                            <div className='relative w-full' style={{ paddingBottom: '56.25%' }}>
                                              <iframe
                                                src={link.url.replace('watch?v=', 'embed/')}
                                                title={link.label}
                                                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                                allowFullScreen
                                                className='absolute inset-0 h-full w-full rounded-xl'
                                              />
                                            </div>
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                      {link.url !== '#' && (
                                        <a
                                          href={link.url}
                                          target='_blank'
                                          rel='noopener noreferrer'
                                          className='inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-primary transition mt-1 pointer-events-auto w-fit'
                                        >
                                          <OpenInNewRounded sx={{ fontSize: 13 }} />
                                          Abrir en YouTube
                                        </a>
                                      )}
                                    </>
                                  )}
                                  {link.type === 'external' && (
                                    <a
                                      href={link.url}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary hover:text-primary/80 transition pointer-events-auto w-fit'
                                    >
                                      <OpenInNewRounded sx={{ fontSize: 16 }} />
                                      {link.label}
                                    </a>
                                  )}
                                  {link.type === 'download' && (
                                    <a
                                      href={link.url}
                                      download
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary hover:text-primary/80 transition pointer-events-auto w-fit'
                                    >
                                      <FileDownloadOutlined sx={{ fontSize: 16 }} />
                                      {link.label}
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {isTourActive && (
                            <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-3 pointer-events-auto">
                              {/* Checkbox: no mostrar en próximas sesiones */}
                              <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                                <input
                                  type="checkbox"
                                  checked={doNotShowAgain}
                                  onChange={e => setDoNotShowAgain(e.target.checked)}
                                  className="h-3.5 w-3.5 rounded border-slate-300 accent-primary cursor-pointer"
                                />
                                <span className="text-[11px] text-slate-400">
                                  No mostrar de nuevo al iniciar sesión
                                </span>
                              </label>

                              <div className="flex items-center justify-between gap-2">
                                <button
                                  type="button"
                                  onClick={handleSkipTour}
                                  className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 transition"
                                >
                                  Saltar
                                </button>
                                <div className="flex gap-2">
                                  {tourStepIndex > 0 && (
                                    <button
                                      type="button"
                                      onClick={handlePrevStep}
                                      className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50 transition"
                                    >
                                      Atrás
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={handleNextStep}
                                    className="rounded-full bg-primary px-3 py-1 text-[11px] font-bold text-white hover:bg-primary/90 shadow-sm transition"
                                  >
                                    {tourStepIndex === tourSteps.length - 1 ? 'Finalizar' : 'Siguiente'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
                </aside>
              </Draggable>
            </motion.div>
          ) : (
            <div
              className='absolute inset-0 pointer-events-none'
            >
              <Draggable
                handle='.minimized-drag-handle'
                nodeRef={minimizedWidgetRef}
                position={minimizedDraggablePosition}
                onStart={handleMinimizedDragStart}
                onDrag={handleMinimizedDrag}
                onStop={handleMinimizedDragStop}
              >
                <div
                  ref={minimizedWidgetRef}
                  className='absolute bottom-0 left-0 flex flex-col items-start gap-3 pointer-events-auto'
                >
              <AnimatePresence>
                {isChatOpen ? (
                  <motion.div
                    key='assistant-pet-chat-panel'
                    initial={{ opacity: 0, y: 16, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 12, scale: 0.96 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className='w-full max-w-[26rem]'
                  >
                    <AssistantPetChatPanel onClose={() => setIsChatOpen(false)} />
                  </motion.div>
                ) : null}
              </AnimatePresence>

              <div className={`minimized-drag-handle group flex items-center gap-3 touch-none ${isDraggingMinimized ? 'cursor-grabbing' : 'cursor-grab'}`}>
                <button
                  type='button'
                  onClick={() => { if (minimizedJustDraggedRef.current) return; handleExpand() }}
                  onMouseEnter={() => setIsHoveringMascot(true)}
                  onMouseLeave={() => setIsHoveringMascot(false)}
                  className='relative'
                  aria-label='Abrir ayuda de LUCA'
                  title='Abrir ayuda de LUCA'
                >
                  {/* Tooltip: Iniciar tour */}
                  <AnimatePresence>
                    {isHoveringMascot && (
                      <motion.div
                        key='restart-tour-tooltip'
                        initial={{ opacity: 0, y: 6, scale: 0.92 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.92 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        onMouseEnter={() => setIsHoveringTourTooltip(true)}
                        onMouseLeave={() => setIsHoveringTourTooltip(false)}
                        className='absolute -top-11 left-0 z-10 pointer-events-auto'
                      >
                        <button
                          type='button'
                          onClick={e => { e.stopPropagation(); if (minimizedJustDraggedRef.current) return; handleRestartTour() }}
                          className='whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-primary shadow-lg hover:bg-slate-50 transition'
                        >
                          ▶ Iniciar tour
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.span
                    animate={{ y: [0, -3, 0] }}
                    transition={{
                      duration: 2.8,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                    className='relative flex h-16 w-16 items-center justify-center rounded-full border border-white/80 bg-primary shadow-2xl ring-1 ring-slate-200/80 sm:h-20 sm:w-20'
                  >
                    <span className='absolute inset-2 rounded-full bg-primary/15 blur-xl' aria-hidden />
                    <img
                      src={robot}
                      alt='Luca AI'
                      draggable={false}
                      className='relative h-20 w-20 object-contain sm:h-16 sm:w-16'
                    />
                  </motion.span>
                </button>

                <button
                  type='button'
                  onClick={() => { if (minimizedJustDraggedRef.current) return; toggleChatPanel() }}
                  className={`inline-flex h-12 items-center gap-2 rounded-full border border-white/90 bg-white px-4 text-sm font-semibold text-primary shadow-[0_18px_34px_rgba(15,23,42,0.14)] transition hover:-translate-y-0.5 hover:bg-slate-50 sm:h-14 sm:px-5 ${isChatOpen ? 'opacity-100 pointer-events-auto' : isHoveringTourTooltip ? 'opacity-0 pointer-events-none' : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto'}`}
                  aria-label='Abrir chat IA de LUCA'
                  title='Abrir chat IA de LUCA'
                >
                  <span className='inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white shadow-[0_10px_20px_rgba(15,23,42,0.18)]'>
                    <ChatRounded sx={{ fontSize: 18 }} />
                  </span>
                  <span>{isChatOpen ? 'Cerrar chat' : 'TryLuca (AI)'}</span>
                </button>
              </div>
                </div>
              </Draggable>
            </div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
export default AssistantPet
